import { escapeHtml } from './escapeHtml';
import { formatSkippedComment } from './formatSkippedComment';
import { serializeBlock } from './serializeBlock';
import type { SerializeOptions } from './serializeOptions';
import { orderSections } from '../helpers/orderSections';
import type { CastBlockContent, CastRoot, CastSection, CastSectionType } from '../types';

/**
 * Renders a Cast tree as an HTML-ish string. Only nodes with `included` set
 * to true are emitted — sections, paragraphs, tables, rows, and cells are
 * gated; descendants of excluded nodes are dropped wholesale. Selection
 * markers always emit when present.
 *
 * Sections are emitted in canonical order (headers → body → footers, each by
 * variant; see `orderSections`) using semantic <header>/<main>/<footer> tags,
 * regardless of their order in `root.children`. A header/footer whose rendered
 * content is identical to one already emitted of the same kind is dropped, so a
 * mirror-duplicate variant (commonly the even-page header) isn't repeated.
 * Absent or excluded sections produce no output.
 */
export function serializeCast(root: CastRoot, options: SerializeOptions): string {
  const parts: string[] = [];
  const seenContentByType = new Map<CastSectionType, Set<string>>();

  for (const section of orderSections(root.children)) {
    const rendered = renderSection(section, options);
    if (rendered === null) continue;

    /**
     * Dedupe header/footer variants by content (the body is single and never
     * deduped). Canonical ordering means the lower-ranked variant — typically
     * `default` — is emitted and any identical later variant is dropped.
     */
    if (section.sectionType !== 'body') {
      const seen = seenContentByType.get(section.sectionType) ?? new Set<string>();
      if (seen.has(rendered.inner)) continue;
      seen.add(rendered.inner);
      seenContentByType.set(section.sectionType, seen);
    }

    parts.push(rendered.wrapped);
  }

  return parts.join('\n');
}

const TAG_BY_SECTION_TYPE: Record<CastSectionType, 'footer' | 'header' | 'main'> = {
  body: 'main',
  footer: 'footer',
  header: 'header',
};

/**
 * Wraps a section's already-serialized `inner` content in its tag:
 * `<tag attrs…>\n${inner}\n</tag>`. The open tag carries a `variant` attribute
 * for non-default header/footer variants and the section's dominant font when
 * those options are on. Exported so `estimateWrapperCost` can size the wrapper
 * from the same code that emits it (passing an empty `inner`), keeping the
 * budget estimate from drifting away from the markup.
 */
export function wrapSection(
  section: CastSection,
  options: SerializeOptions,
  inner: string
): string {
  const tagName = TAG_BY_SECTION_TYPE[section.sectionType];
  const sectionStyle = section.primaryStyle;

  const attrs: string[] = [];
  if (section.sectionType !== 'body' && section.variant !== 'default') {
    attrs.push(`variant="${section.variant}"`);
  }
  if (options.emitFontFamily && sectionStyle?.fontFamily !== undefined) {
    attrs.push(`font-family="${escapeHtml(sectionStyle.fontFamily)}"`);
  }
  if (options.emitFontSize && sectionStyle?.fontSize !== undefined) {
    attrs.push(`font-size="${sectionStyle.fontSize}"`);
  }
  const openTag = attrs.length > 0 ? `<${tagName} ${attrs.join(' ')}>` : `<${tagName}>`;
  return `${openTag}\n${inner}\n</${tagName}>`;
}

function renderSection(
  section: CastSection,
  options: SerializeOptions
): { inner: string; wrapped: string } | null {
  if (section.included !== true) return null;

  const inner = renderSectionChildren(section.children, options, section.primaryStyle);

  /**
   * Skip the wrapper when no content survives filtering — there's no point
   * emitting `<main></main>` for a fully-excluded body. (A fully-excluded
   * section never reaches here: its `included` is not true.)
   */
  if (inner === '') return null;

  return { inner, wrapped: wrapSection(section, options, inner) };
}

/**
 * Serializes a section's blocks, newline-joined. When `emitSkipped` is on, a
 * run of dropped (excluded) blocks is replaced by a single comment marking the
 * gap, e.g. `<!-- skipped 24 paragraphs and 2 tables -->`, placed where the
 * content was.
 */
function renderSectionChildren(
  children: CastBlockContent[],
  options: SerializeOptions,
  sectionStyle: CastSection['primaryStyle']
): string {
  const parts: string[] = [];
  let skippedParagraphs = 0;
  let skippedTables = 0;

  const flushSkipped = (): void => {
    if (options.emitSkipped) {
      const comment = formatSkippedComment([
        { count: skippedParagraphs, noun: 'paragraph' },
        { count: skippedTables, noun: 'table' },
      ]);
      if (comment !== null) parts.push(comment);
    }
    skippedParagraphs = 0;
    skippedTables = 0;
  };

  for (const block of children) {
    const rendered = serializeBlock(block, options, sectionStyle);
    if (rendered === null) {
      if (block.type === 'paragraph') skippedParagraphs += 1;
      else skippedTables += 1;
      continue;
    }
    flushSkipped();
    parts.push(rendered);
  }
  flushSkipped();

  return parts.join('\n');
}
